import { useState } from 'react'
import { FileText, Plus, Link2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, useSupabaseInsert, formatDate, formatTime } from '../hooks/useSupabase'
import { Button, Card, Textarea, Select, Modal, EmptyState, PageHeader, Badge, Toast } from '../components/ui/SharedUI'

export function EvidencePage() {
  const { user } = useAuth()
  const { data: notes, loading, refetch } = useSupabaseQuery('evidence_notes', { orderBy: 'created_at', ascending: false })
  const { data: shifts } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { insert } = useSupabaseInsert('evidence_notes')

  const [modalOpen, setModalOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [form, setForm] = useState({ text: '', shift_id: '', date: new Date().toISOString().split('T')[0] })

  async function handleSubmit(e) {
    e.preventDefault()
    try {
      await insert({
        caregiver_id: user.id,
        text: form.text,
        shift_id: form.shift_id || null,
        date: form.date,
      })
      setModalOpen(false)
      setForm({ text: '', shift_id: '', date: new Date().toISOString().split('T')[0] })
      setToast({ message: 'Note saved. It cannot be edited or deleted — this is by design.', type: 'success' })
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  const recentShifts = shifts.filter(s => s.clock_out).slice(0, 10)

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '800px' }}>
      <PageHeader
        title="Evidence notes"
        description="Timestamped, tamper-evident notes. Once saved, they cannot be edited or deleted."
        action={<Button onClick={() => setModalOpen(true)}><Plus size={16} /> Add note</Button>}
      />

      {notes.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No evidence notes yet"
          description="Add a timestamped note about something that happened — a no-show, a conversation, an incident. These notes are permanent and tamper-evident."
          action={<Button onClick={() => setModalOpen(true)}><Plus size={16} /> Add your first note</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {notes.map(note => (
            <Card key={note.id} style={{ borderLeft: '3px solid #2C7A7B' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A202C' }}>{formatDate(note.date)}</span>
                  {note.shift_id && <Badge variant="default"><Link2 size={11} /> Linked to shift</Badge>}
                </div>
                <span style={{ fontSize: '12px', color: '#A0AEC0' }}>
                  {new Date(note.created_at).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                  })}
                </span>
              </div>
              <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {note.text}
              </p>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#A0AEC0', fontStyle: 'italic' }}>
                Permanently recorded. Cannot be edited or deleted.
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add evidence note">
        <form onSubmit={handleSubmit}>
          <div style={{
            padding: '10px 12px',
            background: '#FCF5E8',
            color: '#8b5000',
            borderRadius: '6px',
            fontSize: '13px',
            marginBottom: '1rem',
            lineHeight: 1.5,
          }}>
            This note will be timestamped at save and cannot be edited or deleted afterward. This is what makes it evidentiary.
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="note-date" style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>Date</label>
            <input
              id="note-date"
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px' }}
              required
            />
          </div>

          <Textarea
            label="What happened?"
            id="text"
            value={form.text}
            onChange={e => setForm({ ...form, text: e.target.value })}
            required
            placeholder="e.g. Dorrie texted at 6:18am saying she wasn't coming. No replacement was sent."
            style={{ minHeight: '120px' }}
          />

          {recentShifts.length > 0 && (
            <Select
              label="Link to a shift (optional)"
              id="shift_id"
              value={form.shift_id}
              onChange={e => setForm({ ...form, shift_id: e.target.value })}
              options={[
                { value: '', label: 'No linked shift' },
                ...recentShifts.map(s => ({
                  value: s.id,
                  label: `${formatDate(s.date)} · ${formatTime(s.clock_in)} – ${formatTime(s.clock_out)}`,
                })),
              ]}
            />
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={!form.text.trim()}>Save note permanently</Button>
          </div>
        </form>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
